<script lang="ts">
    import { onMount } from 'svelte';
    import { experimentService } from '$lib/services/experiment';

    import type { StoryFeedItem } from '@api/story/types';
    import type { Experiment } from '@api/types';
    import {
        type MainGenderBlockContent,
        MainGenderBlockStories,
        MainInfoBlock,
        type MainInfoBlockContent,
        MainWrapper,
    } from '@components/public/main';
    import { LL } from '@i18n/i18n-svelte';
    import { APP_NAME } from '@shared/constants';

    export let data: { stories: StoryFeedItem[]; experiment: Experiment | null };

    const genderBlockContent: MainGenderBlockContent = {
        title: $LL.main.title({ appName: APP_NAME }),
        subtitle: $LL.main.subtitle({ appName: APP_NAME }),
        buttonMaleLabel: $LL.main.buttonMaleLabel(),
        buttonFemaleLabel: $LL.main.buttonFemaleLabel(),
        buttonStartLabel: $LL.main.buttonStartLabel(),
    };
    const infoBlockContent: MainInfoBlockContent = {
        sec1: {
            title: $LL.main.sec1.title({ appName: APP_NAME }),
            text: $LL.main.sec1.text({ appName: APP_NAME }),
            imgAlt: `Video chat with girls on ${APP_NAME}`,
            imgSrc: '/images/landings/main/video-chat-with-girls.png',
        },
        sec2: {
            title: $LL.main.sec2.title({ appName: APP_NAME }),
            articles: [
                {
                    title: $LL.main.sec2.intimate.title(),
                    text: $LL.main.sec2.intimate.text({ appName: APP_NAME }),
                    imgAlt: $LL.main.sec2.intimate.imgAlt({ appName: APP_NAME }),
                    imgSrc: '/images/landings/main/random-video-call-with-girl.png',
                },
                {
                    title: $LL.main.sec2.exclusive.title(),
                    text: $LL.main.sec2.exclusive.text({ appName: APP_NAME }),
                    imgAlt: $LL.main.sec2.exclusive.imgAlt({ appName: APP_NAME }),
                    imgSrc: '/images/landings/main/video-chat-girls.png',
                },
                {
                    title: $LL.main.sec2.seamless.title(),
                    text: $LL.main.sec2.seamless.text(),
                    imgAlt: $LL.main.sec2.seamless.imgAlt({ appName: APP_NAME }),
                    imgSrc: '/images/landings/main/video-chat-with-women.png',
                },
                {
                    title: $LL.main.sec2.private.title(),
                    text: $LL.main.sec2.private.text({ appName: APP_NAME }),
                    imgAlt: $LL.main.sec2.private.imgAlt({ appName: APP_NAME }),
                    imgSrc: '/images/landings/main/anonymous-video-chat.png',
                },
            ],
        },
        sec3: {
            title: $LL.main.sec3.title({ appName: APP_NAME }),
            articles: [
                {
                    title: $LL.main.sec3.randomized.title(),
                    text: $LL.main.sec3.randomized.text({ appName: APP_NAME }),
                    imgSrc: '/images/landings/main/opposite-sex-chat-partner.png',
                    imgAlt: $LL.main.sec3.randomized.imgAlt({ appName: APP_NAME }),
                },
                {
                    title: $LL.main.sec3.engaging.title(),
                    text: $LL.main.sec3.engaging.text(),
                    imgSrc: '/images/landings/main/flirt-video-chat.png',
                    imgAlt: $LL.main.sec3.engaging.imgAlt({ appName: APP_NAME }),
                },
                {
                    title: $LL.main.sec3.endless.title(),
                    text: $LL.main.sec3.endless.text(),
                    imgSrc: '/images/landings/main/flirt-chat-with-strangers.png',
                    imgAlt: $LL.main.sec3.endless.imgAlt({ appName: APP_NAME }),
                },
            ],
        },
        sec4: {
            title: $LL.main.sec4.title({ appName: APP_NAME }),
            text: $LL.main.sec4.text(),
            articles: [
                {
                    title: '1 000 000',
                    text: $LL.main.sec4.popular.text(),
                    imgSrc: '/images/landings/main/sex-chat-roulette.png',
                    imgAlt: $LL.main.sec4.popular.imgAlt({ appName: APP_NAME }),
                },
                {
                    title: '10 000',
                    text: $LL.main.sec4.active.text(),
                    imgSrc: '/images/landings/main/random-sex-chat.png',
                    imgAlt: $LL.main.sec4.active.imgAlt({ appName: APP_NAME }),
                },
                {
                    title: '100',
                    text: $LL.main.sec4.global.text(),
                    imgSrc: '/images/landings/main/live-video-chat-with-girls.png',
                    imgAlt: $LL.main.sec4.global.imgAlt({ appName: APP_NAME }),
                },
            ],
            buttonLabel: $LL.main.sec4.buttonLabel(),
        },
        sec5: {
            title: $LL.main.sec5.title(),
            questions: [
                {
                    title: $LL.main.sec5.unmatched.title({ appName: APP_NAME }),
                    text: $LL.main.sec5.unmatched.text({ appName: APP_NAME }),
                },
                {
                    title: $LL.main.sec5.freemium.title({ appName: APP_NAME }),
                    text: $LL.main.sec5.freemium.text({ appName: APP_NAME }),
                },
                {
                    title: $LL.main.sec5.anonymous.title({ appName: APP_NAME }),
                    text: $LL.main.sec5.anonymous.text(),
                },
                {
                    title: $LL.main.sec5.personalized.title(),
                    text: $LL.main.sec5.personalized.text({ appName: APP_NAME }),
                },
                {
                    title: $LL.main.sec5.zeroTolerance.title(),
                    text: $LL.main.sec5.zeroTolerance.text(),
                },
                {
                    title: $LL.main.sec5.spontaneous.title(),
                    text: $LL.main.sec5.spontaneous.text(),
                },
                {
                    title: $LL.main.sec5.versatile.title({ appName: APP_NAME }),
                    text: $LL.main.sec5.versatile.text({ appName: APP_NAME }),
                },
            ],
        },
    };

    onMount(() => {
        if (data.experiment) {
            experimentService.mergeCookiesAndConfig({
                isUserExist: false,
                experiments: [data.experiment],
            });
        }
    });
</script>

<svelte:head>
    <title>{$LL.welcomePage.metaTitle({ appName: APP_NAME })}</title>
    <meta name="description" content={$LL.welcomePage.metaDescription({ appName: APP_NAME })} />
    <meta property="og:image" content="/images/landings/main/og-image.png" />
</svelte:head>

<MainWrapper>
    <MainGenderBlockStories stories={data.stories} content={genderBlockContent} />
    <MainInfoBlock content={infoBlockContent} />
</MainWrapper>
